"use client";

import { $n } from "@/lib/format";
import { annualized, daysBetween, todayET } from "@/lib/account-returns";
import { fmtMDY } from "@/app/trades/summary-columns";

type Trade = { openedDate: string; closedDate: string; earlyPnl: number };

export type AnalysisHeaderData = {
  nTrades: number;
  nWins: number;
  totalPnl: number;
  totalCapitalAtRisk: number;
  trades: Trade[];
};

const pct1 = (x: number) => {
  const s = `${(Math.abs(x) * 100).toFixed(1)}%`;
  return x < 0 ? `(${s})` : s;
};
const money = (n: number) => {
  const s = "$" + $n(Math.abs(n));
  return n < 0 ? `(${s})` : s;
};
const tone = (x: number) => (x >= 0 ? "text-green" : "text-red");

/**
 * Four-column summary: number, label, number, label. Without `onStartChange`
 * the "Returns since" date is shown read-only; `returns` replaces the
 * profit-over-start-value Total and Annualized figures with ones the caller
 * computed (the Accounts page's time-weighted return). `pnl` supplies the
 * profit figures for accounts with no trade ledger (Stock Trading); the
 * trade-count rows then show "—".
 */
export function AnalysisHeaderTable({
  analysis,
  analysisStart,
  origSize,
  onStartChange,
  returns,
  pnl,
  sinceTitle,
  accountValue,
}: {
  analysis: AnalysisHeaderData | null;
  analysisStart: string;
  origSize: number | null;
  onStartChange?: (v: string) => void;
  returns?: { total: number | null; annualized: number | null };
  pnl?: { total: number; today: number } | null;
  sinceTitle?: string;
  /** Current account value, shown next to the "Returns since" date. */
  accountValue?: number | null;
}) {
  const a = analysis && analysis.nTrades > 0 ? analysis : null;
  const earliestOpen = a
    ? a.trades.reduce((m, t) => (t.openedDate < m ? t.openedDate : m), a.trades[0]?.openedDate ?? "")
    : "";
  const windowStart = analysisStart || earliestOpen;
  // The period runs to today, so idle time after the last exit counts, and
  // the annualized figure compounds rather than scaling linearly.
  const todayISO = todayET();
  const periodDays = windowStart ? Math.max(1, daysBetween(windowStart, todayISO)) : 0;
  const absRor = a && a.totalCapitalAtRisk > 0 ? a.totalPnl / a.totalCapitalAtRisk : 0;
  const rorOrig = returns ? returns.total : a && origSize != null && origSize > 0 ? a.totalPnl / origSize : null;
  const annRorAcct = returns ? returns.annualized : rorOrig != null && periodDays > 0 ? annualized(rorOrig, periodDays) : null;
  const todayPnl = pnl
    ? pnl.today
    : a
      ? a.trades.filter((tr) => tr.closedDate === todayISO).reduce((s, tr) => s + tr.earlyPnl, 0)
      : 0;
  const totalPnl = pnl ? pnl.total : a?.totalPnl ?? 0;

  const tdN = "pr-2 text-right font-mono font-bold tabular-nums whitespace-nowrap";
  const vr = "relative pr-[15px] text-left text-white/70 whitespace-nowrap after:content-[''] after:absolute after:right-0 after:w-px after:bg-[#999]";
  const tdN2 = "pl-[15px] pr-2 text-right font-mono font-bold tabular-nums whitespace-nowrap";
  const tdLLast = "text-left text-white/70 whitespace-nowrap";

  return (
    <table className="m-0 text-[14px] leading-snug">
      <thead>
        <tr>
          <td colSpan={4} className="pb-1.5 border-b border-[#999]">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-white/70">Returns since:</span>
              {!onStartChange ? (
                <span className="font-mono text-foreground" title={sinceTitle}>
                  {analysisStart ? fmtMDY(analysisStart) : "—"}
                </span>
              ) : (<>
              <input
                type="date"
                value={analysisStart}
                onChange={(e) => onStartChange(e.target.value)}
                className="bg-transparent border border-border rounded px-1.5 py-0 text-[14px] text-foreground font-mono"
                title={sinceTitle ?? "Earliest entry date to include in the analysis (saved per account and strategy)"}
              />
              {analysisStart && (
                <button
                  onClick={() => onStartChange("")}
                  className="text-muted hover:text-foreground px-1"
                  title="Clear — analyze all history"
                >
                  ✕
                </button>
              )}
              </>)}
              {accountValue != null && accountValue > 0 && (<>
                <span className="text-white/70 ml-3">Account value:</span>
                <span className="font-mono text-foreground">{money(accountValue)}</span>
              </>)}
            </span>
          </td>
        </tr>
      </thead>
      {(a || pnl) && (
      <tbody className="[&>tr:first-child>td]:pt-1.5">
        <tr>
          <td className={`${tdN} ${tone(totalPnl)}`}>{money(totalPnl)}</td>
          <td className={`${vr} after:top-[5px] after:bottom-0`}>Profit/(loss)</td>
          <td className={`${tdN2} ${rorOrig != null ? tone(rorOrig) : ""}`}>
            {rorOrig != null ? pct1(rorOrig) : "—"}
          </td>
          <td className={tdLLast}>Total Return</td>
        </tr>
        <tr>
          <td className={`${tdN} ${tone(todayPnl)}`}>{money(todayPnl)}</td>
          <td className={`${vr} after:top-0 after:bottom-0`}>Profit/(loss) today</td>
          <td className={`${tdN2} ${a ? tone(absRor) : ""}`}>{a ? pct1(absRor) : "—"}</td>
          <td className={tdLLast}>Return on risk</td>
        </tr>
        <tr>
          <td className={tdN}>{a ? `${a.nWins}/${a.nTrades}/${Math.round((a.nWins / a.nTrades) * 100)}%` : "—"}</td>
          <td className={`${vr} after:top-0 after:bottom-[5px]`}>Winning trades</td>
          <td className={`${tdN2} ${annRorAcct != null ? tone(annRorAcct) : ""}`}>
            {annRorAcct != null ? pct1(annRorAcct) : "—"}
          </td>
          <td className={tdLLast}>Annualized Return</td>
        </tr>
      </tbody>
      )}
    </table>
  );
}
